import { ChangeDetectionStrategy, Component, ElementRef, computed, effect, forwardRef, inject, input, signal } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';

import { BaseControl } from './base-control';

export interface ComboboxOption {
  /** What gets written to the model when picked. */
  value: string;
  /** Main text of the row (e.g. a stop name). */
  label: string;
  /** Secondary text, right-aligned (e.g. the stop id). Matched by the filter too. */
  hint?: string;
}

let uid = 0;

const MENU_MAX_HEIGHT = 288;
const MENU_MIN_WIDTH = 320;
const MENU_GAP = 4;
const VIEWPORT_MARGIN = 8;

/**
 * Free-text input with a filtered suggestion menu — a styled replacement for
 * `<input list>` + `<datalist>`, whose popup the browser draws and can't be
 * styled. Typing filters the options (by label, value or hint, case-
 * insensitive); picking one writes its `value`. Anything typed is still the
 * value, so it never forces a choice. ControlValueAccessor over a string.
 *
 *   <app-combobox [(ngModel)]="stopId" [options]="stopOptions" placeholder="Stop" />
 *
 * The menu reuses `app-select`'s ListMenu look (overlay shadow, 48px rows,
 * down-state wash on the active row, action fill on the current value) and its
 * mechanics: position:fixed at coordinates measured from the field — so a
 * scroll container such as the modal body doesn't clip it — flipping above
 * when the viewport below is tight, closing when the input loses focus (the
 * click-away), on outside mousedown, ancestor scroll or resize. Mousedown
 * inside the menu is preventDefault-ed so picking or scrolling it never blurs
 * the input. It's at least `MENU_MIN_WIDTH` wide (clamped to the viewport) so
 * long labels stay readable from a narrow field.
 *
 * Renders only a bare `<input>` (`inputClass` styles it) so it can sit inside
 * the caller's own chip/shell. At most `maxShown` rows render; the rest are
 * summarised with a "keep typing" footer (`moreLabel`), which keeps very long
 * lists (thousands of GTFS stops) fast.
 *
 * Keyboard: ArrowDown/ArrowUp open and move, Enter picks the active row,
 * Escape closes. Wired as an ARIA combobox (aria-expanded / controls /
 * activedescendant on the input, listbox + options on the menu).
 */
@Component({
  selector: 'app-combobox',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => Combobox), multi: true }],
  host: {
    class: 'block min-w-0',
    '(document:mousedown)': 'onDocumentMouseDown($event)',
  },
  template: `
    <input
      type="text"
      role="combobox"
      autocomplete="off"
      aria-autocomplete="list"
      [attr.aria-expanded]="open()"
      [attr.aria-controls]="listboxId"
      [attr.aria-activedescendant]="open() && activeIndex() >= 0 ? optionId(activeIndex()) : null"
      [attr.aria-label]="ariaLabel() || null"
      [value]="value() ?? ''"
      [placeholder]="placeholder()"
      [disabled]="disabled()"
      (input)="onInput($any($event.target).value)"
      (focus)="openMenu()"
      (click)="openMenu()"
      (keydown)="onKeydown($event)"
      (blur)="onBlur()"
      [class]="inputClass()"
    />

    @if (open() && !disabled() && (shown().length || noMatchLabel())) {
      <div
        role="listbox"
        [id]="listboxId"
        class="fixed z-50 overflow-y-auto overflow-x-hidden rounded-control bg-card shadow-overlay"
        (mousedown)="$event.preventDefault()"
        [style.left.px]="menuLeft()"
        [style.width.px]="menuWidth()"
        [style.top.px]="menuTop()"
        [style.bottom.px]="menuBottom()"
        [style.max-height.px]="menuMaxHeight()"
      >
        @for (opt of shown(); track opt.value; let i = $index) {
          <div
            role="option"
            [id]="optionId(i)"
            [attr.aria-selected]="opt.value === value()"
            (mousedown)="$event.preventDefault(); pick(opt.value)"
            (mouseenter)="activeIndex.set(i)"
            class="row flex h-12 cursor-pointer items-center gap-3 pl-3 pr-3 text-[14px] leading-5"
            [class.selected]="opt.value === value()"
            [class.active]="i === activeIndex() && opt.value !== value()"
          >
            <span class="min-w-0 flex-1 truncate" [class.text-ink-1]="opt.value !== value()">{{ opt.label }}</span>
            @if (opt.hint) {
              <span class="hint flex-none font-num text-[12px]">{{ opt.hint }}</span>
            }
          </div>
        } @empty {
          <div class="flex h-12 items-center px-3 text-[13px] text-ink-3">{{ noMatchLabel() }}</div>
        }
        @if (hiddenCount() > 0 && moreLabel()) {
          <div class="flex h-10 items-center border-t border-hairline px-3 text-[12px] text-ink-3">
            {{ moreLabel().replace('{count}', '' + hiddenCount()) }}
          </div>
        }
      </div>
    }
  `,
  styles: `
    /* ListMenu row states, as in app-select: the active row gets the 10% black
       down-state wash; the current value the action fill in on-button ink. */
    .row .hint {
      color: var(--ink-3);
    }
    .row.active {
      background: var(--overlay-down-1);
    }
    .row.selected {
      background: var(--action-fill);
      color: var(--action-text);
    }
    .row.selected .hint {
      color: var(--action-text);
      opacity: 0.8;
    }
  `,
})
export class Combobox extends BaseControl<string> {
  private readonly host = inject(ElementRef<HTMLElement>);

  readonly options = input<ComboboxOption[]>([]);
  readonly placeholder = input('');
  readonly ariaLabel = input('');
  /** Classes for the bare `<input>` (the caller usually provides the shell). */
  readonly inputClass = input('min-w-0 w-full border-none bg-transparent p-0 outline-none');
  /** Most rows rendered at once; the rest are counted in the footer. */
  readonly maxShown = input(50);
  /** Footer text when rows are cut off; `{count}` is replaced by the hidden count. Translate at the caller. */
  readonly moreLabel = input('');
  /** Shown when the filter matches nothing; empty = hide the menu instead. Translate at the caller. */
  readonly noMatchLabel = input('');

  protected readonly open = signal(false);
  protected readonly activeIndex = signal(-1);

  protected readonly menuLeft = signal(0);
  protected readonly menuWidth = signal(MENU_MIN_WIDTH);
  protected readonly menuTop = signal<number | null>(null);
  protected readonly menuBottom = signal<number | null>(null);
  protected readonly menuMaxHeight = signal(MENU_MAX_HEIGHT);

  protected readonly listboxId = `combobox-listbox-${uid++}`;
  protected optionId(i: number): string {
    return `${this.listboxId}-opt-${i}`;
  }

  /** Options matching what's typed. An exact value match (a picked option)
   *  shows the full list again, so reopening offers every choice. */
  private readonly matches = computed(() => {
    const opts = this.options();
    const term = (this.value() ?? '').trim().toLowerCase();
    if (!term || opts.some((o) => o.value === this.value())) return opts;
    return opts.filter(
      (o) => o.label.toLowerCase().includes(term) || o.value.toLowerCase().includes(term) || !!o.hint?.toLowerCase().includes(term),
    );
  });

  protected readonly shown = computed(() => this.matches().slice(0, this.maxShown()));
  protected readonly hiddenCount = computed(() => Math.max(0, this.matches().length - this.maxShown()));

  constructor() {
    super();
    // Seed the active row on open / when the filter changes: the current value, else the first match.
    effect(() => {
      if (!this.open()) return;
      const rows = this.shown();
      const current = rows.findIndex((o) => o.value === this.value());
      this.activeIndex.set(current >= 0 ? current : rows.length ? 0 : -1);
    });

    // The fixed-position menu doesn't follow its anchor — close on ancestor scroll or resize.
    effect((onCleanup) => {
      if (!this.open()) return;
      const onScroll = (event: Event) => {
        if (this.host.nativeElement.contains(event.target as Node)) return;
        this.close();
      };
      const onResize = () => this.close();
      window.addEventListener('scroll', onScroll, { capture: true, passive: true });
      window.addEventListener('resize', onResize, { passive: true });
      onCleanup(() => {
        window.removeEventListener('scroll', onScroll, { capture: true });
        window.removeEventListener('resize', onResize);
      });
    });
  }

  protected onInput(text: string): void {
    this.setValue(text);
    if (!this.open()) this.openMenu();
  }

  /** Measure the field and place the menu below it (flipping above when tight). */
  protected openMenu(): void {
    if (this.disabled() || this.open() || !this.options().length) return;
    const rect = this.host.nativeElement.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - MENU_GAP - VIEWPORT_MARGIN;
    const spaceAbove = rect.top - MENU_GAP - VIEWPORT_MARGIN;

    const width = Math.min(Math.max(rect.width, MENU_MIN_WIDTH), window.innerWidth - 2 * VIEWPORT_MARGIN);
    this.menuWidth.set(width);
    this.menuLeft.set(Math.max(VIEWPORT_MARGIN, Math.min(rect.left, window.innerWidth - VIEWPORT_MARGIN - width)));
    if (spaceBelow >= MENU_MAX_HEIGHT || spaceBelow >= spaceAbove) {
      this.menuTop.set(rect.bottom + MENU_GAP);
      this.menuBottom.set(null);
      this.menuMaxHeight.set(Math.min(MENU_MAX_HEIGHT, spaceBelow));
    } else {
      this.menuTop.set(null);
      this.menuBottom.set(window.innerHeight - rect.top + MENU_GAP);
      this.menuMaxHeight.set(Math.min(MENU_MAX_HEIGHT, spaceAbove));
    }
    this.open.set(true);
  }

  protected close(): void {
    this.open.set(false);
  }

  /** Leaving the input closes the menu — the reliable "click away": any click
   *  outside moves focus, even on elements that swallow mousedown (e.g. Leaflet).
   *  Clicks inside the menu (rows, scrollbar) preventDefault their mousedown, so
   *  they never blur the input. */
  protected onBlur(): void {
    this.onTouched();
    this.close();
  }

  protected pick(value: string): void {
    this.setValue(value);
    this.onTouched();
    this.close();
  }

  protected onKeydown(event: KeyboardEvent): void {
    if (this.disabled()) return;
    const rows = this.shown();
    if (!this.open()) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        this.openMenu();
      }
      return;
    }
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (rows.length) this.activeIndex.set((this.activeIndex() + 1) % rows.length);
        this.scrollActiveIntoView();
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (rows.length) this.activeIndex.set((this.activeIndex() - 1 + rows.length) % rows.length);
        this.scrollActiveIntoView();
        break;
      case 'Enter': {
        const active = rows[this.activeIndex()];
        if (active) {
          event.preventDefault();
          this.pick(active.value);
        }
        break;
      }
      case 'Escape':
        event.preventDefault();
        this.close();
        break;
      case 'Tab':
        this.close();
        break;
    }
  }

  protected onDocumentMouseDown(event: MouseEvent): void {
    if (!this.open()) return;
    const target = event.target as Node;
    if (this.host.nativeElement.contains(target)) return;
    // The menu is fixed but still inside the host element, so contains() covers it.
    this.close();
  }

  private scrollActiveIntoView(): void {
    const id = this.optionId(this.activeIndex());
    queueMicrotask(() => document.getElementById(id)?.scrollIntoView({ block: 'nearest' }));
  }
}
